/**
 * Report rows → export sheets and display helpers (ADR 0018). Headers come from i18n; operator columns
 * are included only when the user may see operators (the rows then already hide names they may not see).
 */
import { drivingBandLabels, drivingBandShares, type ChecklistComplianceRow, type DrivingMetrics, type DrivingResult, type DrivingRow, type EventRow, type HeatmapCell, type HeatmapMode, type RoutePoint, type RouteSegment, type SpaghettiVehicle, type UsageRow } from "@fts/core";
import type { TFn } from "../i18n";
import { alarmTypeLabel } from "./alarms";
import { systemText, type Sheet } from "./export/sheet";

export function usageSheet(t: TFn, rows: UsageRow[], withOperators: boolean): Sheet {
  return {
    name: t("reports.usage.title"),
    columns: [
      { header: t("reports.col.vehicle"), type: "text", width: 10 },
      { header: t("reports.col.day"), type: "date", width: 12 },
      { header: t("reports.col.ignition"), type: "duration" },
      { header: t("reports.col.moving"), type: "duration" },
      { header: t("reports.col.idle"), type: "duration" },
      { header: t("reports.col.distanceKm"), type: "dec2" },
      { header: t("reports.col.lifts"), type: "int" },
      { header: t("reports.col.liftedKg"), type: "int" },
      { header: t("reports.col.utilisation"), type: "percent" },
      { header: t("reports.col.basis"), type: "text" },
      ...(withOperators ? [{ header: t("reports.col.operators"), type: "text" as const, width: 30 }] : []),
    ],
    rows: rows.map((r) => [
      r.fleetNo, r.day, r.ignitionS, r.movingS, r.idleS, r.distanceM / 1000, r.liftCount, r.liftedKg, r.utilisation,
      t(`reports.basis.${r.basis}`),
      // Without names the cell is FTS's own text ("+1 operatör …"), not user input.
      ...(withOperators
        ? [r.operatorNames.length ? operatorText(t, r.operatorNames, r.operatorsHidden, r.operatorsUnknown) : systemText(operatorText(t, [], r.operatorsHidden, r.operatorsUnknown))]
        : []),
    ]),
  };
}

export function operatorText(t: TFn, names: string[], hidden: number, unknown = 0): string {
  const parts = [...names];
  if (hidden > 0) parts.push(t("reports.operatorsHidden", { count: hidden }));
  if (unknown > 0) parts.push(t("reports.unknownCards", { count: unknown }));
  return parts.join("; ");
}

export function eventsSheet(t: TFn, rows: EventRow[], withOperators: boolean): Sheet {
  return {
    name: t("reports.events.title"),
    columns: [
      { header: t("reports.col.time"), type: "datetime", width: 17 },
      { header: t("reports.col.type"), type: "text", width: 22 },
      { header: t("reports.col.severity"), type: "int" },
      { header: t("reports.col.vehicle"), type: "text" },
      { header: t("reports.col.site"), type: "text", width: 22 },
      ...(withOperators ? [{ header: t("reports.col.operator"), type: "text" as const, width: 18 }] : []),
      { header: t("reports.col.status"), type: "text" },
      { header: t("reports.col.ackedAt"), type: "datetime", width: 17 },
      { header: t("reports.col.ackNote"), type: "text", width: 30 },
      { header: t("reports.col.zone"), type: "text", width: 18 },
      { header: t("reports.col.repeats"), type: "int" },
    ],
    rows: rows.map((r) => [
      r.occurredAt, alarmTypeLabel(t, r.type), r.severity, r.fleetNo, r.siteName,
      ...(withOperators ? [r.operatorName] : []),
      r.status === "acknowledged" ? t("reports.status.acknowledged") : t("reports.status.open"),
      r.ackedAt, r.ackNote, r.geofenceName, r.repeatCount || null,
    ]),
  };
}

export function routePointsSheet(t: TFn, rows: RoutePoint[], withOperators: boolean): Sheet {
  return {
    name: t("reports.route.points"),
    columns: [
      { header: t("reports.col.time"), type: "datetime", width: 17 },
      { header: t("reports.col.lat"), type: "coord", width: 11 },
      { header: t("reports.col.lon"), type: "coord", width: 11 },
      { header: t("reports.col.speed"), type: "dec1" },
      { header: t("reports.col.ignitionOn"), type: "bool" },
      { header: t("reports.col.source"), type: "text" },
      ...(withOperators ? [{ header: t("reports.col.operator"), type: "text" as const, width: 18 }] : []),
    ],
    // Coordinates as text with 6 decimals (~0.1 m): spreadsheets must not round or localise them.
    rows: rows.map((p) => [p.ts, p.lat, p.lon, p.speedKmh, p.ignition, p.src, ...(withOperators ? [p.operatorName] : [])]),
  };
}

export function routeSegmentsSheet(t: TFn, segs: RouteSegment[], withOperators: boolean): Sheet {
  return {
    name: t("reports.route.segments"),
    columns: [
      { header: t("reports.col.segment"), type: "text" },
      { header: t("reports.col.start"), type: "datetime", width: 17 },
      { header: t("reports.col.end"), type: "datetime", width: 17 },
      { header: t("reports.col.duration"), type: "duration" },
      { header: t("reports.col.distanceKm"), type: "dec2" },
      { header: t("reports.col.maxSpeed"), type: "dec1" },
      ...(withOperators ? [{ header: t("reports.col.operators"), type: "text" as const, width: 24 }] : []),
    ],
    rows: segs.map((s) => [
      s.kind === "stop" ? t("reports.route.stop") : t("reports.route.drive"), s.from, s.to, s.durationS, s.distanceM / 1000, s.maxSpeedKmh,
      ...(withOperators ? [s.operators.join("; ")] : []),
    ]),
  };
}

export function checklistSheet(t: TFn, rows: ChecklistComplianceRow[]): Sheet {
  return {
    name: t("reports.checklist.title"),
    columns: [
      { header: t("reports.col.vehicle"), type: "text" },
      { header: t("reports.col.shift"), type: "text", width: 14 },
      { header: t("reports.col.shiftsUsed"), type: "int" },
      { header: t("reports.col.shiftsChecked"), type: "int" },
      { header: t("reports.col.missed"), type: "int" },
      { header: t("reports.col.compliance"), type: "percent" },
      { header: t("reports.col.submissions"), type: "int" },
      { header: t("reports.col.faultsOpened"), type: "int" },
      { header: t("reports.col.faultsResolved"), type: "int" },
      { header: t("reports.col.mttr"), type: "dec1" },
    ],
    rows: rows.map((r) => [
      r.fleetNo, r.hasShifts ? (r.shiftName ?? "—") : t("reports.checklist.noShifts"), r.shiftsUsed, r.shiftsChecked, r.missed, r.compliance,
      r.submissions, r.faultsOpened, r.faultsResolved, r.mttrH,
    ]),
  };
}

/**
 * Heatmap cells (ADR 0024), hottest first: rank, cell centre, weight and count. No identity of any kind —
 * only aggregated positions. `limit` cuts the list (the PDF shows the densest cells only).
 */
export function heatmapCellsSheet(t: TFn, cells: HeatmapCell[], mode: HeatmapMode, limit?: number): Sheet {
  const rows = limit === undefined ? cells : cells.slice(0, limit);
  return {
    name: limit === undefined ? t("reports.heatmap.sheetCells") : t("reports.heatmap.sheetTop", { count: rows.length }),
    columns: [
      { header: t("reports.heatmap.col.rank"), type: "int" },
      { header: t("reports.col.lat"), type: "coord", width: 11 },
      { header: t("reports.col.lon"), type: "coord", width: 11 },
      { header: t("reports.heatmap.col.weight"), type: "int" },
      { header: mode === "events" ? t("reports.heatmap.col.events") : t("reports.heatmap.col.points"), type: "int" },
    ],
    rows: rows.map((c, i) => [i + 1, c.lat, c.lon, Math.round(c.weight), c.count]),
  };
}

/** Spaghetti diagram (ADR 0025): one row per vehicle. No operator columns (positions per vehicle only). */
export function spaghettiSummarySheet(t: TFn, vehicles: SpaghettiVehicle[]): Sheet {
  return {
    name: t("reports.spaghetti.sheetVehicles"),
    columns: [
      { header: t("reports.col.vehicle"), type: "text", width: 10 },
      { header: t("reports.spaghetti.col.movingDistanceKm"), type: "dec2" },
      { header: t("reports.col.moving"), type: "duration" },
      { header: t("reports.spaghetti.col.lines"), type: "int" },
      { header: t("reports.spaghetti.col.points"), type: "int" },
      { header: t("reports.spaghetti.col.movingPoints"), type: "int" },
    ],
    rows: vehicles.map((v) => [v.name, v.distanceM / 1000, v.movingS, v.segments.length, v.points, v.movingPoints]),
  };
}

/** The drawn points (thinned on the server, at most 50 000 in a file): vehicle, line, order, time, position, speed. No operator. */
export function spaghettiPointsSheet(t: TFn, vehicles: SpaghettiVehicle[]): Sheet {
  const rows: (string | number)[][] = [];
  for (const v of vehicles)
    v.segments.forEach((seg, i) => seg.forEach(([lat, lon, ts, spd], j) => rows.push([v.name, i + 1, j + 1, new Date(ts * 1000).toISOString(), lat, lon, spd])));
  return {
    name: t("reports.spaghetti.sheetPoints"),
    columns: [
      { header: t("reports.col.vehicle"), type: "text", width: 10 },
      { header: t("reports.spaghetti.col.line"), type: "int" },
      { header: t("reports.spaghetti.col.order"), type: "int" },
      { header: t("reports.col.time"), type: "datetimeS", width: 19 },
      { header: t("reports.col.lat"), type: "coord", width: 11 },
      { header: t("reports.col.lon"), type: "coord", width: 11 },
      { header: t("reports.col.speed"), type: "dec1" },
    ],
    rows,
  };
}

/** The label of a driving row: the operator's name or fleet number, else FTS's own text for the aggregate rows. */
export function drivingRowLabel(t: TFn, r: Pick<DrivingRow, "kind" | "name" | "cards">): string {
  if (r.kind === "hidden") return t("reports.operatorsHidden", { count: r.cards });
  if (r.kind === "unknown_card") return t("reports.unknownCards", { count: r.cards });
  if (r.kind === "no_card") return t("reports.driving.noCard");
  return r.name ?? "—";
}

/** Event columns of the driving report: impacts by level, then every other event type of the result. */
export function drivingEventColumns(res: Pick<DrivingResult, "eventTypes">): string[] {
  return res.eventTypes.filter((x) => x !== "impact");
}

/**
 * Driving behaviour (ADR 0026): one row per operator or vehicle and a totals row. Transparent numbers only:
 * time, distance, speeds, the share of moving time per speed band, events by type and impact level, rates.
 */
export function drivingSheet(t: TFn, res: DrivingResult): Sheet {
  const byOp = res.group === "operator";
  const others = drivingEventColumns(res);
  const bands = drivingBandLabels(res.bandsKmh);
  const width = 5 + bands.length + 3 + others.length + 3;
  const cells = (m: DrivingMetrics) => [
    m.movingS, m.ignitionS, m.distanceM / 1000, m.avgKmh, m.maxKmh,
    ...drivingBandShares(m.bandS),
    m.impacts[0], m.impacts[1], m.impacts[2],
    ...others.map((x) => m.events[x] ?? 0),
    m.eventsTotal, m.eventsPer10h, m.impactsPer10h,
  ];
  return {
    name: t("reports.driving.title"),
    columns: [
      { header: byOp ? t("reports.col.operator") : t("reports.col.vehicle"), type: "text", width: byOp ? 26 : 10 },
      ...(byOp ? [{ header: t("reports.driving.col.vehicles"), type: "int" as const }] : []),
      { header: t("reports.col.moving"), type: "duration" },
      { header: t("reports.col.ignition"), type: "duration" },
      { header: t("reports.col.distanceKm"), type: "dec2" },
      { header: t("reports.driving.col.avgKmh"), type: "dec1" },
      { header: t("reports.driving.col.maxKmh"), type: "dec1" },
      ...bands.map((b) => ({ header: t("reports.driving.col.band", { band: b }), type: "percent" as const })),
      ...[1, 2, 3].map((l) => ({ header: t("reports.driving.col.impact", { level: l }), type: "int" as const })),
      ...others.map((x) => ({ header: alarmTypeLabel(t, x), type: "int" as const })),
      { header: t("reports.driving.col.events"), type: "int" },
      { header: t("reports.driving.col.eventsRate", { h: res.rateHours }), type: "dec2" },
      { header: t("reports.driving.col.impactsRate", { h: res.rateHours }), type: "dec2" },
    ],
    rows: [
      ...res.rows.map((r) => [
        // Names are user text (formula guard applies); the aggregate rows are FTS's own text.
        r.kind === "operator" || r.kind === "vehicle" ? (r.name ?? "—") : systemText(drivingRowLabel(t, r)),
        // KVKK: a single hidden card — the count (in the label) only, no metric in the file either.
        ...(byOp ? [r.suppressed ? null : r.vehicles] : []),
        ...(r.suppressed ? Array<null>(width).fill(null) : cells(r)),
      ]),
      ...(res.rows.length ? [[systemText(t("reports.totals")), ...(byOp ? [res.totals.vehicles] : []), ...cells(res.totals)]] : []),
    ],
  };
}

/** "5 sa 20 dk" / "5 h 20 min" for screens (files use h:mm). */
export function durationLabel(t: TFn, seconds: number): string {
  const m = Math.round(seconds / 60);
  const h = Math.floor(m / 60);
  return h > 0 ? t("reports.duration.hm", { h, m: m % 60 }) : t("reports.duration.m", { m });
}
